import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { API_BASE, getImageUrl } from '../utils/api';

import moon from '../assets/img/svg/moon.svg'
import verified from '../assets/img/svg/verified.svg'
import user from '../assets/img/svg/user-1.svg'

const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: false,
};

interface DBProperty {
    _id: string; title: string; price: number; status: string; type: string;
    city?: string; address?: string; bedrooms?: number; bathrooms?: number; area?: string;
    images?: string[]; isActive: boolean; isFeatured?: boolean; createdAt: string;
    owner?: { name: string; role: string };
}

export default function ListProperty({ item }: { item: DBProperty }) {
    const propertyImages = item.images && item.images.length > 0 ? item.images : ['/assets/img/p-1.jpg'];

    const [avgRating, setAvgRating] = useState(0)
    const [reviewCount, setReviewCount] = useState(0)

    useEffect(() => {
        fetch(`${API_BASE}/reviews/stats`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ propertyIds: [item._id] })
        })
            .then(r => r.json())
            .then(data => {
                if (data[item._id]) {
                    setAvgRating(data[item._id].avgRating)
                    setReviewCount(data[item._id].count)
                }
            })
            .catch(() => { })
    }, [item._id])

    const renderStars = (rating: number) => {
        const stars = []
        for (let i = 1; i <= 5; i++) {
            stars.push(
                <i key={i} className={`fa-solid fa-star ${i <= Math.round(rating) ? 'text-yellow-400' : 'text-gray-200'}`} style={{ fontSize: '10px', margin: '0 1px' }}></i>
            )
        }
        return stars
    }

    return (
        <div className="group bg-white rounded-3xl overflow-hidden transition-all duration-500 hover:shadow-2xl hover:-translate-y-1 mb-6 border border-gray-100 flex flex-col md:flex-row p-3 gap-6">
            {/* Image Section */}
            <div className="md:w-1/3 lg:w-1/4 relative">
                <div className="relative rounded-2xl overflow-hidden h-full min-h-[220px]">
                    {/* Status Badges */}
                    <div className="absolute top-3 left-3 z-10 flex flex-wrap gap-2">
                        {item.isFeatured && (
                            <div className="bg-purple-600/90 backdrop-blur-md text-white text-[8px] font-bold uppercase tracking-wider px-2 py-1 rounded-md flex items-center shadow-lg">
                                <img src={user} alt="" className="w-2.5 h-2.5 me-1 brightness-0 invert" /> Featured
                            </div>
                        )}
                        <div className="bg-green-600/90 backdrop-blur-md text-white text-[8px] font-bold uppercase tracking-wider px-2 py-1 rounded-md flex items-center shadow-lg">
                            <img src={verified} alt="" className="w-2.5 h-2.5 me-1 brightness-0 invert" /> Verified
                        </div>
                    </div>

                    <Slider {...settings} className="property-slider-inner h-full">
                        {propertyImages.map((el: any, index: number) => (
                            <div key={index} className="h-full">
                                <Link to={`/property-details/${item._id}`}>
                                    <img 
                                        src={getImageUrl(el)} 
                                        className="w-full h-[250px] md:h-full object-cover transition-transform duration-700 group-hover:scale-110" 
                                        alt={item.title} 
                                    />
                                </Link>
                            </div>
                        ))}
                    </Slider>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none"></div>
                </div>
            </div>

            {/* Content Section */}
            <div className="md:w-2/3 lg:w-3/4 flex flex-col justify-between py-2 pr-2">
                <div>
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                        <div className="flex items-center gap-2">
                            {item.status === "For Rent" && (
                                <span className="bg-green-50 text-green-600 text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full border border-green-100">
                                    For Rent
                                </span>
                            )}
                            {item.status === "For Sale" && (
                                <span className="bg-red-50 text-red-600 text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full border border-red-100">
                                    For Sale
                                </span>
                            )}
                            <span className="bg-purple-50 text-purple-600 text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full border border-purple-100">
                                {item.type}
                            </span>
                        </div>
                        <h6 className="text-2xl font-black text-primary tracking-tight">₹{item.price?.toLocaleString()}</h6>
                    </div>

                    <h4 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                        <Link to={`/property-details/${item._id}`}>{item.title}</Link>
                    </h4>

                    <div className="flex items-center text-gray-400 text-sm mb-6">
                        <i className="fa-solid fa-location-dot me-2 text-primary/40"></i>
                        <span>{item.city || item.address || 'Location'}</span>
                    </div>

                    {/* Features Strip */}
                    <div className="flex flex-wrap items-center gap-8 mb-6">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-gray-50 flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                                <i className="fa-solid fa-building-shield text-gray-400 group-hover:text-primary transition-colors text-xs"></i>
                            </div>
                            <span className="text-[11px] font-bold text-gray-600 uppercase tracking-wide">{item.type}</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-gray-50 flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                                <i className="fa-solid fa-bed text-gray-400 group-hover:text-primary transition-colors text-xs"></i>
                            </div>
                            <span className="text-[11px] font-bold text-gray-600 uppercase tracking-wide">{item.bedrooms || 0} Beds</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-gray-50 flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                                <i className="fa-solid fa-maximize text-gray-400 group-hover:text-primary transition-colors text-xs"></i>
                            </div>
                            <span className="text-[11px] font-bold text-gray-600 uppercase tracking-wide">{item.area || 0} sqft</span>
                        </div>
                    </div>
                </div>

                {/* Footer Area */}
                <div className="flex items-center justify-between pt-6 border-t border-gray-50 mt-auto">
                    <div className="flex items-center gap-3">
                        {reviewCount > 0 ? (
                            <div className="flex items-center gap-2">
                                <div className="bg-primary/10 text-primary font-black text-xs px-2 py-1 rounded-lg">
                                    {avgRating}
                                </div>
                                <div className="flex">
                                    {renderStars(avgRating)}
                                </div>
                                <span className="text-gray-400 text-[10px] font-bold uppercase tracking-widest ms-1">({reviewCount} reviews)</span>
                            </div>
                        ) : (
                            <span className="text-gray-400 text-[10px] font-bold uppercase tracking-widest">No reviews yet</span>
                        )}
                    </div>
                    <Link 
                        to={`/property-details/${item._id}`} 
                        className="bg-primary-dark hover:bg-primary text-white text-[11px] font-black uppercase tracking-[0.2em] px-8 py-3.5 rounded-2xl transition-all duration-300 shadow-lg shadow-primary-dark/20 hover:-translate-y-0.5 active:scale-95"
                    >
                        View Details
                    </Link>
                </div>
            </div>
        </div>
    )
}

